/* Source: /home/sidsolaiyappan/skooch_ai/shared/narration_verifier.jac */
import {__jacJsx, __jacSpawn} from "@jac/runtime";
import { dayNameFromIso, shortDateLabel } from "./action_outcomes.js";
const _jac = {
  int: {
    bit_length(n) {
      if (n === 0) return 0;
      return Math.floor(Math.log2(Math.abs(n))) + 1;
    },
    bit_count(n) {
      let x = Math.abs(n);
      let count = 0;
      while (x) { count += x & 1; x >>= 1; }
      return count;
    },
    to_bytes(n, length, byteorder) {
      const bytes = [];
      let val = n < 0 ? (1 << (length * 8)) + n : n;
      for (let i = 0; i < length; i++) {
        bytes.push(val & 0xff);
        val >>= 8;
      }
      if (byteorder === "big") bytes.reverse();
      return bytes;
    },
    from_bytes(bytes, byteorder) {
      const b = byteorder === "big" ? bytes : [...bytes].reverse();
      let result = 0;
      for (const byte of b) result = (result << 8) | byte;
      return result;
    },
    mod(a, b) { return ((a % b) + b) % b; }
  },

  float: {
    as_integer_ratio(f) {
      if (!isFinite(f)) throw new _jac.exc.ValueError("cannot convert " + f + " to integer ratio");
      if (f === 0) return [0, 1];
      let num = f, den = 1;
      while (num !== Math.floor(num)) { num *= 2; den *= 2; }
      const g = _jac.float._gcd(Math.abs(num), den);
      return [num / g, den / g];
    },
    _gcd(a, b) { while (b) { [a, b] = [b, a % b]; } return a; },
    hex(f) {
      return f.toString(16);
    },
    fromhex(s) {
      return parseFloat(s);
    },
    mod(a, b) { return ((a % b) + b) % b; }
  },

  complex: {
    conjugate(c) { return {re: c.re, im: -c.im}; },
    add(a, b) { return {re: a.re + b.re, im: a.im + b.im}; },
    sub(a, b) { return {re: a.re - b.re, im: a.im - b.im}; },
    mul(a, b) { return {re: a.re * b.re - a.im * b.im, im: a.re * b.im + a.im * b.re}; },
    truediv(a, b) {
      const d = b.re * b.re + b.im * b.im;
      return {re: (a.re * b.re + a.im * b.im) / d, im: (a.im * b.re - a.re * b.im) / d};
    },
    pow(a, b) {
      if (typeof b === "number") b = {re: b, im: 0};
      if (typeof a === "number") a = {re: a, im: 0};
      if (b.im === 0 && Number.isInteger(b.re)) {
        let result = {re: 1, im: 0};
        for (let i = 0; i < Math.abs(b.re); i++) result = _jac.complex.mul(result, a);
        return b.re < 0 ? _jac.complex.truediv({re: 1, im: 0}, result) : result;
      }
      const r = Math.sqrt(a.re * a.re + a.im * a.im);
      const theta = Math.atan2(a.im, a.re);
      const lnr = Math.log(r);
      const newR = Math.exp(lnr * b.re - theta * b.im);
      const newTheta = lnr * b.im + theta * b.re;
      return {re: newR * Math.cos(newTheta), im: newR * Math.sin(newTheta)};
    },
    eq(a, b) {
      if (typeof b === "number") b = {re: b, im: 0};
      if (typeof a === "number") a = {re: a, im: 0};
      return a.re === b.re && a.im === b.im;
    },
    neg(a) { return {re: -a.re, im: -a.im}; },
    pos(a) { return {re: +a.re, im: +a.im}; }
  },

  str: {
    capitalize(s) {
      if (s.length === 0) return s;
      return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    },
    title(s) {
      return s.replace(/\b\w/g, c => c.toUpperCase());
    },
    swapcase(s) {
      return [...s].map(c => c === c.toUpperCase() ? c.toLowerCase() : c.toUpperCase()).join("");
    },
    count(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      if (sub === "") return t.length + 1;
      let count = 0, pos = 0;
      while ((pos = t.indexOf(sub, pos)) !== -1) { count++; pos += sub.length; }
      return count;
    },
    find(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      const idx = t.indexOf(sub);
      return idx === -1 ? -1 : idx + (start || 0);
    },
    rfind(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      const idx = t.lastIndexOf(sub);
      return idx === -1 ? -1 : idx + (start || 0);
    },
    index(s, sub, start, end) {
      const idx = _jac.str.find(s, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    rindex(s, sub, start, end) {
      const idx = _jac.str.rfind(s, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    startswith(s, prefix, start, end) {
      return s.slice(start || 0, end).startsWith(prefix);
    },
    endswith(s, suffix, start, end) {
      return s.slice(start || 0, end).endsWith(suffix);
    },
    replace(s, old, new_, count) {
      if (count === undefined) return s.split(old).join(new_);
      let result = s, n = 0;
      while (n < count) {
        const idx = result.indexOf(old);
        if (idx === -1) break;
        result = result.slice(0, idx) + new_ + result.slice(idx + old.length);
        n++;
      }
      return result;
    },
    strip(s, chars) {
      if (!chars) return s.trim();
      let start = 0, end = s.length;
      while (start < end && chars.indexOf(s[start]) !== -1) start++;
      while (end > start && chars.indexOf(s[end - 1]) !== -1) end--;
      return s.slice(start, end);
    },
    lstrip(s, chars) {
      if (!chars) return s.trimStart();
      let start = 0;
      while (start < s.length && chars.indexOf(s[start]) !== -1) start++;
      return s.slice(start);
    },
    rstrip(s, chars) {
      if (!chars) return s.trimEnd();
      let end = s.length;
      while (end > 0 && chars.indexOf(s[end - 1]) !== -1) end--;
      return s.slice(0, end);
    },
    removeprefix(s, prefix) {
      return s.startsWith(prefix) ? s.slice(prefix.length) : s;
    },
    removesuffix(s, suffix) {
      return (suffix && s.endsWith(suffix)) ? s.slice(0, -suffix.length) : s;
    },
    split(s, sep, maxsplit) {
      if (sep === undefined || sep === null) {
        const parts = s.trim().split(/\s+/);
        if (maxsplit === undefined) return parts;
        if (maxsplit <= 0) return [s];
        return [...parts.slice(0, maxsplit), parts.slice(maxsplit).join(" ")].filter(x => x !== undefined);
      }
      if (maxsplit === undefined) return s.split(sep);
      const parts = s.split(sep);
      if (parts.length <= maxsplit + 1) return parts;
      return [...parts.slice(0, maxsplit), parts.slice(maxsplit).join(sep)];
    },
    rsplit(s, sep, maxsplit) {
      if (maxsplit === undefined) return _jac.str.split(s, sep);
      const parts = s.split(sep);
      if (parts.length <= maxsplit + 1) return parts;
      const keep = parts.length - maxsplit;
      return [parts.slice(0, keep).join(sep), ...parts.slice(keep)];
    },
    splitlines(s, keepends) {
      const lines = s.split(/(\r\n|\r|\n)/);
      const result = [];
      for (let i = 0; i < lines.length; i += 2) {
        const line = lines[i];
        const sep = lines[i + 1] || "";
        if (line || sep) result.push(keepends ? line + sep : line);
      }
      return result;
    },
    partition(s, sep) {
      const idx = s.indexOf(sep);
      if (idx === -1) return [s, "", ""];
      return [s.slice(0, idx), sep, s.slice(idx + sep.length)];
    },
    rpartition(s, sep) {
      const idx = s.lastIndexOf(sep);
      if (idx === -1) return ["", "", s];
      return [s.slice(0, idx), sep, s.slice(idx + sep.length)];
    },
    format(s, ...args) {
      let i = 0;
      return s.replace(/\{(\w*)\}/g, (match, key) => {
        if (key === "") return args[i++];
        if (!isNaN(key)) return args[parseInt(key)];
        return args[0] && typeof args[0] === "object" ? args[0][key] : match;
      });
    },
    format_map(s, mapping) {
      return s.replace(/\{(\w+)\}/g, (match, key) => key in mapping ? mapping[key] : match);
    },
    center(s, width, fillchar) {
      const fc = fillchar || " ";
      if (s.length >= width) return s;
      const total = width - s.length;
      const left = Math.floor(total / 2);
      return fc.repeat(left) + s + fc.repeat(total - left);
    },
    zfill(s, width) {
      if (s.length >= width) return s;
      const sign = (s[0] === "+" || s[0] === "-") ? s[0] : "";
      const body = sign ? s.slice(1) : s;
      return sign + "0".repeat(width - s.length) + body;
    },
    expandtabs(s, tabsize) {
      const ts = tabsize === undefined ? 8 : tabsize;
      let result = "", col = 0;
      for (const c of s) {
        if (c === "\t") { const sp = ts - (col % ts); result += " ".repeat(sp); col += sp; }
        else if (c === "\n" || c === "\r") { result += c; col = 0; }
        else { result += c; col++; }
      }
      return result;
    },
    isnumeric(s) {
      if (s.length === 0) return false;
      return [...s].every(c => /[\d\u00B2\u00B3\u00B9\u00BC-\u00BE\u2150-\u218F]/.test(c));
    },
    istitle(s) {
      return s === _jac.str.title(s) && /[A-Za-z]/.test(s);
    },
    translate(s, table) {
      return [...s].map(c => {
        const code = c.charCodeAt(0);
        if (code in table) {
          const v = table[code];
          if (v === null) return "";
          if (typeof v === "number") return String.fromCharCode(v);
          return v;
        }
        return c;
      }).join("");
    },
    maketrans(...args) {
      const table = {};
      if (args.length === 1) {
        const mapping = args[0];
        for (const [k, v] of Object.entries(mapping)) {
          table[typeof k === "string" ? k.charCodeAt(0) : k] = v;
        }
      } else if (args.length >= 2) {
        const from_ = args[0], to_ = args[1];
        for (let i = 0; i < from_.length; i++) {
          table[from_.charCodeAt(i)] = to_.charCodeAt(i);
        }
        if (args[2]) {
          for (const c of args[2]) table[c.charCodeAt(0)] = null;
        }
      }
      return table;
    },
    mod(s, args) {
      let i = 0;
      const a = Array.isArray(args) ? args : [args];
      return s.replace(/%([sd%])/g, (m, fmt) => {
        if (fmt === "%") return "%";
        return String(a[i++]);
      });
    }
  },

  // Runtime-polymorphic method dispatch, used when the codegen cannot prove
  // the static type of a method-call receiver (e.g. a `dict`-typed prop, the
  // result of `or` widening, or a comprehension over an untyped value) and so
  // cannot pick a typed emitter. The compiler emits
  // `_jac.poly.call(obj, "name", ...args)`; `call` resolves the *runtime* type
  // of `obj` and applies the matching Python operation, forwards to a
  // same-named method on a user-defined object, or raises AttributeError. The
  // per-type tables below carry the trivial native-mapped methods inline and
  // delegate the non-trivial ones to the shared `_jac.str/list/dict/set`
  // helpers, so each operation has a single implementation and no Python
  // method name can leak verbatim into the emitted JavaScript.
  poly: {
    _typename(o) {
      if (o === null || o === undefined) return "NoneType";
      if (Array.isArray(o)) return "list";
      if (o instanceof Set) return "set";
      if (o instanceof Map) return "dict";
      if (o instanceof Uint8Array) return "bytes";
      const t = typeof o;
      if (t === "string") return "str";
      if (t === "boolean") return "bool";
      if (t === "number") return Number.isInteger(o) ? "int" : "float";
      if (t === "object") return (o.constructor && o.constructor.name) || "object";
      return t;
    },
    // --- per-runtime-type Python method tables (keyed by Python name) ---
    // Trivial native-mapped ops are inline; non-trivial ones delegate to the
    // shared `_jac.<type>` helpers (which the typed emitters also use), so the
    // typed and polymorphic paths share one implementation.
    _str: {
      lower: (s) => s.toLowerCase(),
      upper: (s) => s.toUpperCase(),
      casefold: (s) => s.toLowerCase(),
      strip: (s, chars) => {
        if (chars === undefined) return s.trim();
        let i = 0, j = s.length;
        while (i < j && chars.indexOf(s[i]) !== -1) i++;
        while (j > i && chars.indexOf(s[j - 1]) !== -1) j--;
        return s.slice(i, j);
      },
      lstrip: (s, chars) => {
        if (chars === undefined) return s.replace(/^\s+/, "");
        let i = 0;
        while (i < s.length && chars.indexOf(s[i]) !== -1) i++;
        return s.slice(i);
      },
      rstrip: (s, chars) => {
        if (chars === undefined) return s.replace(/\s+$/, "");
        let j = s.length;
        while (j > 0 && chars.indexOf(s[j - 1]) !== -1) j--;
        return s.slice(0, j);
      }
    },
    _list: {
      append: (a, x) => { a.push(x); return null; },
      clear: (a) => { a.length = 0; return null; },
      copy: (a) => a.slice(),
      count: (a, x) => _jac.list.count(a, x),
      extend: (a, b) => { for (const v of b) a.push(v); return null; },
      index: (a, x, start, end) => _jac.list.index(a, x, start, end),
      insert: (a, i, x) => { a.splice(i, 0, x); return null; },
      pop: (a, i) => {
        if (i === undefined) return a.pop();
        const idx = i < 0 ? a.length + i : i;
        return a.splice(idx, 1)[0];
      },
      remove: (a, x) => _jac.list.remove(a, x),
      reverse: (a) => { a.reverse(); return null; },
      sort: (a, key) => _jac.list.sort(a, key)
    },
    _dict: {
      clear: (o) => _jac.dict.clear(o),
      copy: (o) => ({ ...o }),
      // Python `dict.get`: returns the stored value when the key is present
      // (even if it is null), and the default only when the key is absent.
      // A membership check is required — `o[key] ?? default_` would wrongly
      // substitute the default for a present-but-null value.
      get: (o, key, default_) =>
        Object.prototype.hasOwnProperty.call(o, key) ? o[key] : default_,
      items: (o) => Object.entries(o),
      keys: (o) => Object.keys(o),
      pop: (o, key, default_) => _jac.dict.pop(o, key, default_),
      popitem: (o) => _jac.dict.popitem(o),
      setdefault: (o, key, default_) => _jac.dict.setdefault(o, key, default_),
      update: (o, other) => {
        if (other instanceof Map) { for (const [k, v] of other) o[k] = v; }
        else if (Array.isArray(other)) { for (const [k, v] of other) o[k] = v; }
        else if (other) { for (const k of Object.keys(other)) o[k] = other[k]; }
        return null;
      },
      values: (o) => Object.values(o)
    },
    _set: {
      add: (s, x) => { s.add(x); return null; },
      clear: (s) => { s.clear(); return null; },
      copy: (s) => new Set(s),
      difference: (a, b) => _jac.set.difference(a, b),
      difference_update: (s, o) => _jac.set.difference_update(s, o),
      discard: (s, x) => { s.delete(x); return null; },
      intersection: (a, b) => _jac.set.intersection(a, b),
      intersection_update: (s, o) => _jac.set.intersection_update(s, o),
      isdisjoint: (a, b) => _jac.set.isDisjointFrom(a, b instanceof Set ? b : new Set(b)),
      issubset: (a, b) => _jac.set.isSubsetOf(a, b instanceof Set ? b : new Set(b)),
      issuperset: (a, b) => _jac.set.isSupersetOf(a, b instanceof Set ? b : new Set(b)),
      pop: (s) => _jac.set.pop(s),
      remove: (s, x) => _jac.set.remove(s, x),
      symmetric_difference: (a, b) => _jac.set.symmetricDifference(a, b),
      symmetric_difference_update: (s, o) => _jac.set.symmetric_difference_update(s, o),
      union: (a, b) => _jac.set.union(a, b),
      update: (s, o) => _jac.set.update(s, o)
    },
    // Jac dicts compile to plain objects, but interop code may hand us a real
    // Map; mirror the dict methods over the Map API for that case.
    _map: {
      clear: (m) => { m.clear(); return null; },
      copy: (m) => new Map(m),
      get: (m, key, default_) => (m.has(key) ? m.get(key) : default_),
      items: (m) => [...m.entries()],
      keys: (m) => [...m.keys()],
      pop: (m, key, default_) => {
        if (m.has(key)) { const v = m.get(key); m.delete(key); return v; }
        if (default_ !== undefined) return default_;
        throw new _jac.exc.KeyError("KeyError: " + key);
      },
      popitem: (m) => {
        if (m.size === 0) throw new _jac.exc.KeyError("dictionary is empty");
        const key = [...m.keys()].pop();
        const val = m.get(key);
        m.delete(key);
        return [key, val];
      },
      setdefault: (m, key, default_) => {
        if (!m.has(key)) m.set(key, default_ === undefined ? null : default_);
        return m.get(key);
      },
      update: (m, other) => {
        if (other instanceof Map) { for (const [k, v] of other) m.set(k, v); }
        else if (Array.isArray(other)) { for (const [k, v] of other) m.set(k, v); }
        else if (other) { for (const k of Object.keys(other)) m.set(k, other[k]); }
        return null;
      },
      values: (m) => [...m.values()]
    },
    // Single entry point. `obj.method(...args)` on a receiver whose static
    // type is not provably primitive is compiled to
    // `_jac.poly.call(obj, "method", ...args)`.
    call(o, m, ...args) {
      // 1. Genuine JS primitives get Python-semantic dispatch: a plain object
      //    is a Python dict, an Array is a list, a Set/Map/string/number/bytes
      //    maps to its Python counterpart. These are the types whose Python
      //    method semantics differ from (or have no) native JS equivalent.
      if (typeof o === "string") {
        if (_jac.poly._str[m]) return _jac.poly._str[m](o, ...args);
        if (_jac.str[m]) return _jac.str[m](o, ...args);
      } else if (typeof o === "number") {
        const ns = Number.isInteger(o) ? _jac.int : _jac.float;
        if (ns && ns[m]) return ns[m](o, ...args);
      } else if (Array.isArray(o)) {
        if (_jac.poly._list[m]) return _jac.poly._list[m](o, ...args);
      } else if (o instanceof Set) {
        if (_jac.poly._set[m]) return _jac.poly._set[m](o, ...args);
      } else if (o instanceof Map) {
        if (_jac.poly._map[m]) return _jac.poly._map[m](o, ...args);
      } else if (o instanceof Uint8Array) {
        if (_jac.bytes[m]) return _jac.bytes[m](o, ...args);
      } else if (
        o !== null && o !== undefined && typeof o === "object"
        && (Object.getPrototypeOf(o) === Object.prototype
            || Object.getPrototypeOf(o) === null)
      ) {
        // A plain object is a Python dict literal.
        if (_jac.poly._dict[m]) return _jac.poly._dict[m](o, ...args);
      }
      // 2. Otherwise forward to a same-named method the receiver carries
      //    itself: a user-defined class instance, a host/framework object
      //    (Headers, URLSearchParams, ...), a callable with attached methods,
      //    or a Set/Map/Array method not mirrored above. This matches the
      //    direct `obj.method(...)` that a typed call would have emitted.
      if (o !== null && o !== undefined && typeof o[m] === "function") {
        return o[m](...args);
      }
      // 3. Nothing applies: raise the Python-style error instead of leaking a
      //    method name or silently returning a wrong value.
      throw new _jac.exc.AttributeError(
        "'" + _jac.poly._typename(o) + "' object has no attribute '" + m + "'");
    },
    // Backward-compatible named shims. The compiler now emits `call`, but a
    // previously-built bundle may still reference a named helper; these route
    // it through the same dispatcher (rest params preserve the arg count).
    lower(s, ...r) { return _jac.poly.call(s, "lower", ...r); },
    upper(s, ...r) { return _jac.poly.call(s, "upper", ...r); },
    strip(s, ...r) { return _jac.poly.call(s, "strip", ...r); },
    lstrip(s, ...r) { return _jac.poly.call(s, "lstrip", ...r); },
    rstrip(s, ...r) { return _jac.poly.call(s, "rstrip", ...r); },
    append(a, ...r) { return _jac.poly.call(a, "append", ...r); },
    extend(a, ...r) { return _jac.poly.call(a, "extend", ...r); },
    pop(a, ...r) { return _jac.poly.call(a, "pop", ...r); },
    get(o, ...r) { return _jac.poly.call(o, "get", ...r); },
    contains(c, x) {
      // Python-equivalent membership for a value of unknown static type.
      // string -> substring; array (list/tuple/range) -> element;
      // Set/Map -> key/element; plain object (dict) -> own key.
      if (typeof c === "string") return c.includes(x);
      if (Array.isArray(c)) return c.includes(x);
      if (c instanceof Set || c instanceof Map) return c.has(x);
      if (c !== null && typeof c === "object") {
        return Object.prototype.hasOwnProperty.call(c, x);
      }
      return false;
    },
    getitem(c, i) {
      // Subscript read for a value of unknown static type. This is a faithful
      // superset of a raw `c[i]`: it only adds Python's negative indexing for
      // sequences (string/array) and otherwise preserves JS semantics exactly
      // (mapping/object access, out-of-range -> `undefined`, no new throws).
      if (typeof i === "number" && i < 0
          && (typeof c === "string" || Array.isArray(c))) {
        return c[c.length + i];
      }
      if (c instanceof Map) return c.get(i);
      return c[i];
    },
    setitem(c, i, v) {
      // Subscript assignment for a value of unknown/list static type. A
      // negative index writes the array element counted from the end (a raw
      // JS `c[-1] = v` would instead create a bogus "-1" property); every
      // other case behaves like a plain `c[i] = v`.
      if (typeof i === "number" && i < 0 && Array.isArray(c)) {
        c[c.length + i] = v;
        return v;
      }
      if (c instanceof Map) { c.set(i, v); return v; }
      c[i] = v;
      return v;
    },
    and_(a, bf) {
      // Python `a and b`: returns `a` when it is falsy, else evaluates and
      // returns `b`. `bf` is a thunk so the right operand keeps short-circuit
      // (lazy) evaluation, and `a` is evaluated exactly once by the caller.
      return _jac.builtin.bool(a) ? bf() : a;
    },
    or_(a, bf) {
      // Python `a or b`: returns `a` when it is truthy, else `b`.
      return _jac.builtin.bool(a) ? a : bf();
    }
  },

  list: {
    remove(arr, val) {
      const i = arr.indexOf(val);
      if (i === -1) throw new _jac.exc.ValueError("list.remove(x): x not in list");
      arr.splice(i, 1);
    },
    index(arr, val, start, end) {
      const s = start || 0;
      const e = end === undefined ? arr.length : end;
      for (let i = s; i < e; i++) {
        if (arr[i] === val) return i;
      }
      throw new _jac.exc.ValueError(val + " is not in list");
    },
    count(arr, val) {
      let c = 0;
      for (const item of arr) { if (item === val) c++; }
      return c;
    },
    sort(arr, key) {
      if (key) {
        arr.sort((a, b) => {
          const ka = key(a), kb = key(b);
          return ka < kb ? -1 : ka > kb ? 1 : 0;
        });
      } else {
        arr.sort((a, b) => a < b ? -1 : a > b ? 1 : 0);
      }
    },
    repeat(arr, n) {
      const result = [];
      for (let i = 0; i < n; i++) result.push(...arr);
      return result;
    },
    eq(a, b) { return JSON.stringify(a) === JSON.stringify(b); },
    lt(a, b) {
      for (let i = 0; i < Math.min(a.length, b.length); i++) {
        if (a[i] < b[i]) return true;
        if (a[i] > b[i]) return false;
      }
      return a.length < b.length;
    },
    gt(a, b) { return _jac.list.lt(b, a); },
    le(a, b) { return _jac.list.eq(a, b) || _jac.list.lt(a, b); },
    ge(a, b) { return _jac.list.eq(a, b) || _jac.list.gt(a, b); },
    imul(arr, n) {
      const orig = [...arr];
      arr.length = 0;
      for (let i = 0; i < n; i++) arr.push(...orig);
      return arr;
    }
  },

  dict: {
    pop(d, key, default_) {
      if (key in d) { const v = d[key]; delete d[key]; return v; }
      if (default_ !== undefined) return default_;
      throw new _jac.exc.KeyError("KeyError: " + key);
    },
    popitem(d) {
      const keys = Object.keys(d);
      if (keys.length === 0) throw new _jac.exc.KeyError("dictionary is empty");
      const key = keys[keys.length - 1];
      const val = d[key];
      delete d[key];
      return [key, val];
    },
    setdefault(d, key, default_) {
      if (!(key in d)) d[key] = default_ === undefined ? null : default_;
      return d[key];
    },
    clear(d) {
      for (const key of Object.keys(d)) delete d[key];
    },
    fromkeys(keys, value) {
      const d = {};
      const v = value === undefined ? null : value;
      for (const k of keys) d[k] = v;
      return d;
    },
    eq(a, b) {
      const ka = Object.keys(a), kb = Object.keys(b);
      if (ka.length !== kb.length) return false;
      for (const k of ka) {
        if (!(k in b)) return false;
        const va = a[k], vb = b[k];
        if (va !== vb) {
          if (typeof va === "object" && typeof vb === "object" && va !== null && vb !== null) {
            if (JSON.stringify(va) !== JSON.stringify(vb)) return false;
          } else return false;
        }
      }
      return true;
    }
  },

  set: {
    remove(s, val) {
      if (!s.has(val)) throw new _jac.exc.KeyError("KeyError: " + val);
      s.delete(val);
    },
    pop(s) {
      if (s.size === 0) throw new _jac.exc.KeyError("pop from an empty set");
      const val = s.values().next().value;
      s.delete(val);
      return val;
    },
    update(s, other) { for (const v of other) s.add(v); },
    intersection_update(s, other) {
      const otherSet = other instanceof Set ? other : new Set(other);
      for (const v of s) { if (!otherSet.has(v)) s.delete(v); }
    },
    difference_update(s, other) {
      for (const v of other) s.delete(v);
    },
    symmetric_difference_update(s, other) {
      for (const v of other) { if (s.has(v)) s.delete(v); else s.add(v); }
    },
    union(a, b) {
      const r = new Set(a);
      for (const v of b) r.add(v);
      return r;
    },
    intersection(a, b) {
      const bs = b instanceof Set ? b : new Set(b);
      const r = new Set();
      for (const v of a) { if (bs.has(v)) r.add(v); }
      return r;
    },
    difference(a, b) {
      const bs = b instanceof Set ? b : new Set(b);
      const r = new Set();
      for (const v of a) { if (!bs.has(v)) r.add(v); }
      return r;
    },
    symmetricDifference(a, b) {
      const r = new Set(a);
      for (const v of b) { if (r.has(v)) r.delete(v); else r.add(v); }
      return r;
    },
    isSubsetOf(a, b) {
      if (a.size > b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    },
    isSupersetOf(a, b) { return _jac.set.isSubsetOf(b, a); },
    isDisjointFrom(a, b) {
      for (const v of a) { if (b.has(v)) return false; }
      return true;
    },
    is_proper_subset(a, b) {
      if (a.size >= b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    },
    is_proper_superset(a, b) { return _jac.set.is_proper_subset(b, a); },
    eq(a, b) {
      if (a.size !== b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    }
  },

  bytes: {
    hex(b) {
      return Array.from(b).map(x => x.toString(16).padStart(2, "0")).join("");
    },
    fromhex(s) {
      const arr = [];
      const clean = s.replace(/\s/g, "");
      for (let i = 0; i < clean.length; i += 2) arr.push(parseInt(clean.substr(i, 2), 16));
      return new Uint8Array(arr);
    },
    _indexOf(b, sub, start, end) {
      const s = start || 0;
      const e = end === undefined ? b.length : end;
      const sl = sub.length;
      if (sl === 0) return s;
      for (let i = s; i <= e - sl; i++) {
        let m = true;
        for (let j = 0; j < sl; j++) { if (b[i + j] !== sub[j]) { m = false; break; } }
        if (m) return i;
      }
      return -1;
    },
    _lastIndexOf(b, sub, start, end) {
      const s = start || 0;
      const e = end === undefined ? b.length : end;
      const sl = sub.length;
      if (sl === 0) return e;
      for (let i = e - sl; i >= s; i--) {
        let m = true;
        for (let j = 0; j < sl; j++) { if (b[i + j] !== sub[j]) { m = false; break; } }
        if (m) return i;
      }
      return -1;
    },
    count(b, sub) {
      let c = 0, p = 0;
      while (p <= b.length - sub.length) {
        const idx = _jac.bytes._indexOf(b, sub, p);
        if (idx === -1) break;
        c++; p = idx + sub.length;
      }
      return c;
    },
    find(b, sub, start, end) { return _jac.bytes._indexOf(b, sub, start, end); },
    rfind(b, sub, start, end) { return _jac.bytes._lastIndexOf(b, sub, start, end); },
    index(b, sub, start, end) {
      const idx = _jac.bytes._indexOf(b, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    rindex(b, sub, start, end) {
      const idx = _jac.bytes._lastIndexOf(b, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    startswith(b, prefix, start, end) {
      const s = start || 0;
      if (prefix.length > b.length - s) return false;
      for (let i = 0; i < prefix.length; i++) { if (b[s + i] !== prefix[i]) return false; }
      return true;
    },
    endswith(b, suffix, start, end) {
      const e = end === undefined ? b.length : end;
      if (suffix.length > e) return false;
      const off = e - suffix.length;
      for (let i = 0; i < suffix.length; i++) { if (b[off + i] !== suffix[i]) return false; }
      return true;
    },
    replace(b, old_, new_, count) {
      const r = [];
      let pos = 0, n = 0;
      const max = count === undefined ? Infinity : count;
      while (pos <= b.length) {
        if (n < max) {
          const idx = _jac.bytes._indexOf(b, old_, pos);
          if (idx !== -1) {
            for (let i = pos; i < idx; i++) r.push(b[i]);
            for (let i = 0; i < new_.length; i++) r.push(new_[i]);
            pos = idx + old_.length; n++;
            continue;
          }
        }
        for (let i = pos; i < b.length; i++) r.push(b[i]);
        break;
      }
      return new Uint8Array(r);
    },
    _isWS(c) { return c === 32 || c === 9 || c === 10 || c === 13 || c === 11 || c === 12; },
    strip(b, chars) {
      let s = 0, e = b.length;
      if (chars) { const cs = new Set(chars); while (s < e && cs.has(b[s])) s++; while (e > s && cs.has(b[e-1])) e--; }
      else { while (s < e && _jac.bytes._isWS(b[s])) s++; while (e > s && _jac.bytes._isWS(b[e-1])) e--; }
      return b.slice(s, e);
    },
    lstrip(b, chars) {
      let s = 0;
      if (chars) { const cs = new Set(chars); while (s < b.length && cs.has(b[s])) s++; }
      else { while (s < b.length && _jac.bytes._isWS(b[s])) s++; }
      return b.slice(s);
    },
    rstrip(b, chars) {
      let e = b.length;
      if (chars) { const cs = new Set(chars); while (e > 0 && cs.has(b[e-1])) e--; }
      else { while (e > 0 && _jac.bytes._isWS(b[e-1])) e--; }
      return b.slice(0, e);
    },
    removeprefix(b, prefix) {
      if (_jac.bytes.startswith(b, prefix)) return b.slice(prefix.length);
      return b.slice();
    },
    removesuffix(b, suffix) {
      if (suffix.length > 0 && _jac.bytes.endswith(b, suffix)) return b.slice(0, b.length - suffix.length);
      return b.slice();
    },
    split(b, sep, maxsplit) {
      const parts = [];
      let pos = 0, n = 0;
      const max = maxsplit === undefined ? Infinity : maxsplit;
      while (pos <= b.length) {
        if (n >= max) { parts.push(b.slice(pos)); return parts; }
        const idx = _jac.bytes._indexOf(b, sep, pos);
        if (idx === -1) { parts.push(b.slice(pos)); return parts; }
        parts.push(b.slice(pos, idx));
        pos = idx + sep.length; n++;
      }
      if (pos <= b.length) parts.push(b.slice(pos));
      return parts;
    },
    rsplit(b, sep, maxsplit) {
      if (maxsplit === undefined) return _jac.bytes.split(b, sep);
      const parts = [];
      let end = b.length, n = 0;
      while (end >= 0 && n < maxsplit) {
        const idx = _jac.bytes._lastIndexOf(b, sep, 0, end);
        if (idx === -1) break;
        parts.unshift(b.slice(idx + sep.length, end));
        end = idx; n++;
      }
      parts.unshift(b.slice(0, end));
      return parts;
    },
    splitlines(b, keepends) {
      const result = [];
      let start = 0;
      for (let i = 0; i < b.length; i++) {
        if (b[i] === 10 || b[i] === 13) {
          let eol = i + 1;
          if (b[i] === 13 && i + 1 < b.length && b[i + 1] === 10) eol++;
          result.push(keepends ? b.slice(start, eol) : b.slice(start, i));
          start = eol;
          if (eol > i + 1) i++;
        }
      }
      if (start < b.length) result.push(b.slice(start));
      return result;
    },
    join(sep, parts) {
      if (parts.length === 0) return new Uint8Array(0);
      const r = [];
      for (let i = 0; i < parts.length; i++) {
        if (i > 0) for (const x of sep) r.push(x);
        for (const x of parts[i]) r.push(x);
      }
      return new Uint8Array(r);
    },
    partition(b, sep) {
      const idx = _jac.bytes._indexOf(b, sep);
      if (idx === -1) return [b.slice(), new Uint8Array(0), new Uint8Array(0)];
      return [b.slice(0, idx), sep.slice(), b.slice(idx + sep.length)];
    },
    rpartition(b, sep) {
      const idx = _jac.bytes._lastIndexOf(b, sep);
      if (idx === -1) return [new Uint8Array(0), new Uint8Array(0), b.slice()];
      return [b.slice(0, idx), sep.slice(), b.slice(idx + sep.length)];
    },
    _isUp(c) { return c >= 65 && c <= 90; },
    _isLo(c) { return c >= 97 && c <= 122; },
    _isAl(c) { return (c >= 65 && c <= 90) || (c >= 97 && c <= 122); },
    _isDig(c) { return c >= 48 && c <= 57; },
    capitalize(b) {
      if (b.length === 0) return new Uint8Array(0);
      const r = new Uint8Array(b.length);
      r[0] = _jac.bytes._isLo(b[0]) ? b[0] - 32 : b[0];
      for (let i = 1; i < b.length; i++) r[i] = _jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i];
      return r;
    },
    lower(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) r[i] = _jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i];
      return r;
    },
    upper(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) r[i] = _jac.bytes._isLo(b[i]) ? b[i] - 32 : b[i];
      return r;
    },
    title(b) {
      const r = new Uint8Array(b.length);
      let prev = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isAl(b[i])) {
          r[i] = prev ? (_jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i]) : (_jac.bytes._isLo(b[i]) ? b[i] - 32 : b[i]);
          prev = true;
        } else { r[i] = b[i]; prev = false; }
      }
      return r;
    },
    swapcase(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) r[i] = b[i] + 32;
        else if (_jac.bytes._isLo(b[i])) r[i] = b[i] - 32;
        else r[i] = b[i];
      }
      return r;
    },
    center(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const total = width - b.length;
      const left = Math.floor(total / 2);
      const r = new Uint8Array(width);
      r.fill(f, 0, left); r.set(b, left); r.fill(f, left + b.length);
      return r;
    },
    ljust(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      r.set(b); r.fill(f, b.length);
      return r;
    },
    rjust(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      const off = width - b.length;
      r.fill(f, 0, off); r.set(b, off);
      return r;
    },
    zfill(b, width) {
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      let off = 0;
      if (b.length > 0 && (b[0] === 43 || b[0] === 45)) { r[0] = b[0]; off = 1; }
      const pad = width - b.length;
      r.fill(48, off, off + pad);
      for (let i = off; i < b.length; i++) r[pad + i] = b[i];
      return r;
    },
    expandtabs(b, tabsize) {
      const ts = tabsize === undefined ? 8 : tabsize;
      const r = [];
      let col = 0;
      for (let i = 0; i < b.length; i++) {
        if (b[i] === 9) { const sp = ts - (col % ts); for (let j = 0; j < sp; j++) r.push(32); col += sp; }
        else if (b[i] === 10 || b[i] === 13) { r.push(b[i]); col = 0; }
        else { r.push(b[i]); col++; }
      }
      return new Uint8Array(r);
    },
    translate(b, table, del_) {
      const r = [];
      const ds = del_ ? new Set(del_) : null;
      for (let i = 0; i < b.length; i++) {
        if (ds && ds.has(b[i])) continue;
        r.push(table && table[b[i]] !== undefined ? table[b[i]] : b[i]);
      }
      return new Uint8Array(r);
    },
    maketrans(from_, to_) {
      const t = new Uint8Array(256);
      for (let i = 0; i < 256; i++) t[i] = i;
      for (let i = 0; i < from_.length; i++) t[from_[i]] = to_[i];
      return t;
    },
    isalnum(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isAl(b[i]) && !_jac.bytes._isDig(b[i])) return false;
      return true;
    },
    isalpha(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isAl(b[i])) return false;
      return true;
    },
    isascii(b) {
      for (let i = 0; i < b.length; i++) if (b[i] > 127) return false;
      return true;
    },
    isdigit(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isDig(b[i])) return false;
      return true;
    },
    islower(b) {
      let has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) return false;
        if (_jac.bytes._isLo(b[i])) has = true;
      }
      return has;
    },
    isupper(b) {
      let has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isLo(b[i])) return false;
        if (_jac.bytes._isUp(b[i])) has = true;
      }
      return has;
    },
    isspace(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isWS(b[i])) return false;
      return true;
    },
    istitle(b) {
      let prev = false, has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) { if (prev) return false; prev = true; has = true; }
        else if (_jac.bytes._isLo(b[i])) { if (!prev) return false; prev = true; has = true; }
        else prev = false;
      }
      return has;
    },
    add(a, b) {
      const r = new Uint8Array(a.length + b.length);
      r.set(a); r.set(b, a.length);
      return r;
    },
    mul(b, n) {
      const r = new Uint8Array(b.length * n);
      for (let i = 0; i < n; i++) r.set(b, i * b.length);
      return r;
    },
    mod(b, args) {
      const s = new TextDecoder().decode(b);
      return new TextEncoder().encode(_jac.str.mod(s, args));
    },
    eq(a, b) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
      return true;
    },
    lt(a, b) {
      for (let i = 0; i < Math.min(a.length, b.length); i++) {
        if (a[i] < b[i]) return true;
        if (a[i] > b[i]) return false;
      }
      return a.length < b.length;
    },
    gt(a, b) { return _jac.bytes.lt(b, a); },
    le(a, b) { return _jac.bytes.eq(a, b) || _jac.bytes.lt(a, b); },
    ge(a, b) { return _jac.bytes.eq(a, b) || _jac.bytes.gt(a, b); },
    contains(b, sub) { return _jac.bytes._indexOf(b, sub) !== -1; }
  },

  tuple: {
    count(arr, val) { return _jac.list.count(arr, val); },
    index(arr, val, start, end) { return _jac.list.index(arr, val, start, end); },
    repeat(arr, n) { return _jac.list.repeat(arr, n); },
    eq(a, b) { return _jac.list.eq(a, b); },
    lt(a, b) { return _jac.list.lt(a, b); },
    gt(a, b) { return _jac.list.gt(a, b); },
    le(a, b) { return _jac.list.le(a, b); },
    ge(a, b) { return _jac.list.ge(a, b); }
  },

  range: {
    count(r, val) {
      if (typeof r[Symbol.iterator] === "function") {
        let c = 0;
        for (const v of r) { if (v === val) c++; }
        return c;
      }
      return 0;
    },
    index(r, val) {
      let i = 0;
      for (const v of r) { if (v === val) return i; i++; }
      throw new _jac.exc.ValueError(val + " is not in range");
    },
    eq(a, b) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) { if (a[i] !== b[i]) return false; }
      return true;
    },
    contains(r, val) {
      for (const v of r) { if (v === val) return true; }
      return false;
    }
  },

  builtin: {
    round(n, ndigits) {
      if (ndigits === undefined) return Math.round(n);
      const f = Math.pow(10, ndigits);
      return Math.round(n * f) / f;
    },
    sum(iterable, start) {
      let total = start || 0;
      for (const v of iterable) total += v;
      return total;
    },
    sorted(iterable, key, reverse) {
      const arr = [...iterable];
      if (key) {
        arr.sort((a, b) => {
          const ka = key(a), kb = key(b);
          return ka < kb ? -1 : ka > kb ? 1 : 0;
        });
      } else {
        arr.sort((a, b) => a < b ? -1 : a > b ? 1 : 0);
      }
      if (reverse) arr.reverse();
      return arr;
    },
    enumerate(iterable, start) {
      const s = start || 0;
      return [...iterable].map((v, i) => [s + i, v]);
    },
    zip(...iterables) {
      const arrays = iterables.map(it => [...it]);
      const len = Math.min(...arrays.map(a => a.length));
      const result = [];
      for (let i = 0; i < len; i++) {
        result.push(arrays.map(a => a[i]));
      }
      return result;
    },
    map(fn, ...iterables) {
      if (iterables.length === 1) return [...iterables[0]].map(fn);
      const arrays = iterables.map(it => [...it]);
      const len = Math.min(...arrays.map(a => a.length));
      const result = [];
      for (let i = 0; i < len; i++) {
        result.push(fn(...arrays.map(a => a[i])));
      }
      return result;
    },
    filter(fn, iterable) {
      return [...iterable].filter(fn || (x => x));
    },
    // Python subclass relation over builtin-type *tags* -- the string names
    // (`"str"`, `"int"`, ...) that `exit_builtin_type` lowers builtin types to.
    // `object` is the universal base and `bool` is a subclass of `int`; `tuple`
    // and `frozenset` share JS representations with `list`/`set`, so fold those
    // aliases to their runtime tag first. One source of truth so isinstance and
    // issubclass can never disagree.
    _tagSubclass(subTag, superTag) {
      const fold = (t) => t === "tuple" ? "list" : t === "frozenset" ? "set" : t;
      const sub = fold(subTag), sup = fold(superTag);
      if (sup === "object") return true;
      if (sub === sup) return true;
      if (sup === "int" && sub === "bool") return true;
      return false;
    },
    isinstance(o, classinfo) {
      // isinstance(x, (A, B, ...)) -- the tuple of types lowers to a JS array;
      // match if `o` is an instance of any member.
      if (Array.isArray(classinfo)) {
        for (const c of classinfo) {
          if (_jac.builtin.isinstance(o, c)) return true;
        }
        return false;
      }
      // Builtin types lower to a string tag (their Python type name): compare
      // the value's runtime type against it via the shared subclass relation.
      if (typeof classinfo === "string") {
        return _jac.builtin._tagSubclass(_jac.poly._typename(o), classinfo);
      }
      // User archetype / JS constructor.
      if (typeof classinfo === "function") return o instanceof classinfo;
      return false;
    },
    issubclass(cls, classinfo) {
      if (Array.isArray(classinfo)) {
        for (const c of classinfo) {
          if (_jac.builtin.issubclass(cls, c)) return true;
        }
        return false;
      }
      // When either side is a builtin-type tag, compare by the shared subclass
      // relation (covers `object`, `bool` ⊂ `int`, and identity).
      if (typeof cls === "string" || typeof classinfo === "string") {
        return _jac.builtin._tagSubclass(cls, classinfo);
      }
      if (cls === classinfo) return true;
      let proto = cls;
      while (proto) {
        if (proto === classinfo) return true;
        proto = Object.getPrototypeOf(proto);
      }
      return false;
    },
    id(o) {
      if (o === null || o === undefined) return 0;
      if (typeof o === "number") return o;
      return typeof o === "string" ? _jac.builtin._strHash(o) : 0;
    },
    _strHash(s) {
      let h = 0;
      for (let i = 0; i < s.length; i++) {
        h = ((h << 5) - h + s.charCodeAt(i)) | 0;
      }
      return h;
    },
    jid(obj) {
      if (obj === null || obj === undefined) {
        throw new _jac.exc.TypeError(
          "jid() called on " + String(obj)
          + " -- the server may have returned an error instead of a node."
          + " Check that your endpoint returned successfully before using jid()."
        );
      }
      if (obj._jac_id === undefined) {
        throw new _jac.exc.TypeError(
          "jid() expected a node or edge with graph identity, but received "
          + (typeof obj === "object" ? JSON.stringify(Object.keys(obj)) : typeof obj)
          + ". Only node and edge archetypes have a jid."
        );
      }
      return obj._jac_id;
    },
    // unsafe_html(x) returns a sentinel object that the JSX flattener
    // recognises and renders as raw HTML via dangerouslySetInnerHTML.
    // Named "unsafe" so the security implication is visible at the use site.
    unsafe_html(html) {
      return { __jacUnsafeHtml: String(html == null ? "" : html) };
    },
    hash(obj) { return _jac.builtin.id(obj); },
    repr(obj) { return JSON.stringify(obj); },
    pow(base, exp, mod) {
      let result = Math.pow(base, exp);
      if (mod !== undefined) result = ((result % mod) + mod) % mod;
      return result;
    },
    next(iter, default_) {
      const result = iter.next();
      if (result.done) {
        if (default_ !== undefined) return default_;
        throw new _jac.exc.StopIteration("StopIteration");
      }
      return result.value;
    },
    vars(o) {
      if (o === undefined) return {};
      const result = {};
      for (const [k, v] of Object.entries(o)) {
        if (typeof v !== "function") result[k] = v;
      }
      return result;
    },
    dir(o) {
      if (o === undefined) return [];
      return Object.getOwnPropertyNames(o).sort();
    },
    open() { throw new _jac.exc.OSError("open() is not supported in client-side JavaScript"); },
    format(value, format_spec) {
      if (!format_spec) return String(value);
      return String(value);
    },
    ascii(obj) {
      if (typeof obj === "string") {
        let r = "'";
        for (const c of obj) {
          const code = c.charCodeAt(0);
          if (c === "\\") r += "\\\\";
          else if (c === "'") r += "\\'";
          else if (code >= 0x20 && code <= 0x7e) r += c;
          else if (code < 0x100) r += "\\x" + code.toString(16).padStart(2, "0");
          else r += "\\u" + code.toString(16).padStart(4, "0");
        }
        return r + "'";
      }
      return String(obj);
    },
    complex(re, im) { return {re: re || 0, im: im || 0}; },
    bool(x) {
      if (x === null || x === undefined || x === false || x === 0 || x === "" || Number.isNaN(x)) return false;
      if (Array.isArray(x)) return x.length > 0;
      if (x instanceof Set || x instanceof Map) return x.size > 0;
      if (typeof x === "object") return Object.keys(x).length > 0;
      return true;
    },
    range(...args) {
      let start, stop, step;
      if (args.length === 1) { start = 0; stop = args[0]; step = 1; }
      else if (args.length === 2) { start = args[0]; stop = args[1]; step = 1; }
      else { start = args[0]; stop = args[1]; step = args[2]; }
      if (step === 0) throw new _jac.exc.ValueError("range() arg 3 must not be zero");
      const result = [];
      if (step > 0) { for (let i = start; i < stop; i += step) result.push(i); }
      else { for (let i = start; i > stop; i += step) result.push(i); }
      return result;
    },
    slice(...args) {
      if (args.length === 1) return { start: null, stop: args[0], step: null };
      return { start: args[0] !== undefined ? args[0] : null, stop: args[1] !== undefined ? args[1] : null, step: args[2] !== undefined ? args[2] : null };
    },
    format(value, format_spec) {
      if (!format_spec || format_spec === "") return String(value);
      const m = format_spec.match(/^([<>^]?)(\d+)?(?:\.(\d+))?([dfseboxXn%]?)$/);
      if (!m) return String(value);
      const [, align, widthStr, precStr, ftype] = m;
      let s;
      const prec = precStr !== undefined ? parseInt(precStr) : undefined;
      if (ftype === "f" || ftype === "") {
        s = prec !== undefined ? Number(value).toFixed(prec) : String(value);
      } else if (ftype === "d") {
        s = String(Math.floor(Number(value)));
      } else if (ftype === "b") {
        s = Number(value).toString(2);
      } else if (ftype === "o") {
        s = Number(value).toString(8);
      } else if (ftype === "x") {
        s = Number(value).toString(16);
      } else if (ftype === "X") {
        s = Number(value).toString(16).toUpperCase();
      } else if (ftype === "e") {
        s = prec !== undefined ? Number(value).toExponential(prec) : Number(value).toExponential();
      } else if (ftype === "%") {
        s = (prec !== undefined ? (Number(value) * 100).toFixed(prec) : (Number(value) * 100).toFixed(6)) + "%";
      } else if (ftype === "s" || ftype === "n") {
        s = String(value);
      } else {
        s = String(value);
      }
      if (widthStr) {
        const width = parseInt(widthStr);
        if (s.length < width) {
          const pad = " ".repeat(width - s.length);
          if (align === "<") s = s + pad;
          else if (align === "^") {
            const left = Math.floor((width - s.length) / 2);
            s = " ".repeat(left) + s + " ".repeat(width - s.length - left);
          }
          else s = pad + s;
        }
      }
      return s;
    }
  },

  exc: (() => {
    class BaseException extends Error { constructor(m) { super(m); this.name = 'BaseException'; } }
    class Exception extends BaseException { constructor(m) { super(m); this.name = 'Exception'; } }
    class ArithmeticError extends Exception { constructor(m) { super(m); this.name = 'ArithmeticError'; } }
    class ZeroDivisionError extends ArithmeticError { constructor(m) { super(m); this.name = 'ZeroDivisionError'; } }
    class OverflowError extends ArithmeticError { constructor(m) { super(m); this.name = 'OverflowError'; } }
    class FloatingPointError extends ArithmeticError { constructor(m) { super(m); this.name = 'FloatingPointError'; } }
    class LookupError extends Exception { constructor(m) { super(m); this.name = 'LookupError'; } }
    class IndexError extends LookupError { constructor(m) { super(m); this.name = 'IndexError'; } }
    class KeyError extends LookupError { constructor(m) { super(m); this.name = 'KeyError'; } }
    class ValueError extends Exception { constructor(m) { super(m); this.name = 'ValueError'; } }
    class TypeError_ extends Exception { constructor(m) { super(m); this.name = 'TypeError'; } }
    class AttributeError extends Exception { constructor(m) { super(m); this.name = 'AttributeError'; } }
    class RuntimeError extends Exception { constructor(m) { super(m); this.name = 'RuntimeError'; } }
    class NotImplementedError extends RuntimeError { constructor(m) { super(m); this.name = 'NotImplementedError'; } }
    class RecursionError extends RuntimeError { constructor(m) { super(m); this.name = 'RecursionError'; } }
    class OSError extends Exception { constructor(m) { super(m); this.name = 'OSError'; } }
    class FileNotFoundError extends OSError { constructor(m) { super(m); this.name = 'FileNotFoundError'; } }
    class FileExistsError extends OSError { constructor(m) { super(m); this.name = 'FileExistsError'; } }
    class PermissionError extends OSError { constructor(m) { super(m); this.name = 'PermissionError'; } }
    class TimeoutError extends OSError { constructor(m) { super(m); this.name = 'TimeoutError'; } }
    class IsADirectoryError extends OSError { constructor(m) { super(m); this.name = 'IsADirectoryError'; } }
    class NotADirectoryError extends OSError { constructor(m) { super(m); this.name = 'NotADirectoryError'; } }
    class AssertionError extends Exception { constructor(m) { super(m); this.name = 'AssertionError'; } }
    class ImportError extends Exception { constructor(m) { super(m); this.name = 'ImportError'; } }
    class ModuleNotFoundError extends ImportError { constructor(m) { super(m); this.name = 'ModuleNotFoundError'; } }
    class NameError extends Exception { constructor(m) { super(m); this.name = 'NameError'; } }
    class UnboundLocalError extends NameError { constructor(m) { super(m); this.name = 'UnboundLocalError'; } }
    class StopIteration extends Exception { constructor(m) { super(m); this.name = 'StopIteration'; } }
    class StopAsyncIteration extends Exception { constructor(m) { super(m); this.name = 'StopAsyncIteration'; } }
    class EOFError extends Exception { constructor(m) { super(m); this.name = 'EOFError'; } }
    class MemoryError extends Exception { constructor(m) { super(m); this.name = 'MemoryError'; } }
    class ReferenceError_ extends Exception { constructor(m) { super(m); this.name = 'ReferenceError'; } }
    class KeyboardInterrupt extends BaseException { constructor(m) { super(m); this.name = 'KeyboardInterrupt'; } }
    class SystemExit extends BaseException { constructor(m) { super(m); this.name = 'SystemExit'; } }
    class GeneratorExit extends BaseException { constructor(m) { super(m); this.name = 'GeneratorExit'; } }
    return { BaseException, Exception, ArithmeticError, ZeroDivisionError, OverflowError, FloatingPointError, LookupError, IndexError, KeyError, ValueError, TypeError: TypeError_, AttributeError, RuntimeError, NotImplementedError, RecursionError, OSError, FileNotFoundError, FileExistsError, PermissionError, TimeoutError, IsADirectoryError, NotADirectoryError, AssertionError, ImportError, ModuleNotFoundError, NameError, UnboundLocalError, StopIteration, StopAsyncIteration, EOFError, MemoryError, ReferenceError: ReferenceError_, KeyboardInterrupt, SystemExit, GeneratorExit };
  })()
};
function _nvNorm(s) {
  let _q = String.fromCharCode(34);
  let _t = String((((s !== undefined) && (s !== null)) ? s : "")).toLowerCase();
  _t = _jac.poly.call(_jac.poly.call(_jac.poly.call(_jac.poly.call(_t, "split", String.fromCharCode(8217)), "join", "'"), "split", String.fromCharCode(8216)), "join", "'");
  _t = _jac.poly.call(_jac.poly.call(_jac.poly.call(_jac.poly.call(_t, "split", String.fromCharCode(8220)), "join", _q), "split", String.fromCharCode(8221)), "join", _q);
  return _t;
}
function _nvIsLetter(c) {
  if (((c === undefined) || (c === null))) {
    return false;
  }
  return ("abcdefghijklmnopqrstuvwxyz".indexOf(c) >= 0);
}
function _nvExtractDayTokens(lowText) {
  let _t = String((lowText ? lowText : ""));
  let _full = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
  let _canon = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
  let _short = ["mon", "tues", "tue", "wed", "thurs", "thur", "thu", "fri", "sat", "sun"];
  let _shortCanon = {"mon": "mon", "tues": "tue", "tue": "tue", "wed": "wed", "thurs": "thu", "thur": "thu", "thu": "thu", "fri": "fri", "sat": "sat", "sun": "sun"};
  let _out = [];
  let _i = 0;
  let _n = _t.length;
  while ((_i < _n)) {
    if (((_i > 0) && _nvIsLetter(_t.at((_i - 1))))) {
      _i += 1;
      continue;
    }
    let _matched = 0;
    let _fi = 0;
    for (const _f of _full) {
      if (((_matched === 0) && (_t.slice(_i, (_i + _f.length)) === _f))) {
        if (!_nvIsLetter(_t.at((_i + _f.length)))) {
          _out.push(_canon.at(_fi));
          _matched = _f.length;
        }
      }
      _fi += 1;
    }
    if ((_matched === 0)) {
      for (const _s of _short) {
        if (((_matched === 0) && (_t.slice(_i, (_i + _s.length)) === _s))) {
          if (!_nvIsLetter(_t.at((_i + _s.length)))) {
            _out.push(_shortCanon[_s]);
            _matched = _s.length;
          }
        }
      }
    }
    _i += ((_matched > 0) ? _matched : 1);
  }
  return _out;
}
function _nvExtractTimeTokens(lowText) {
  let _t = String((lowText ? lowText : ""));
  let _digits = "0123456789";
  let _out = [];
  let _i = 0;
  let _n = _t.length;
  while ((_i < _n)) {
    if ((_digits.indexOf(_t.at(_i)) < 0)) {
      if (((_i === 0) || !_nvIsLetter(_t.at((_i - 1))))) {
        if (((_t.slice(_i, (_i + 4)) === "noon") && !_nvIsLetter(_t.at((_i + 4))))) {
          _out.push({"h": 12, "m": 0, "mer": "pm"});
          _i += 4;
          continue;
        }
        if (((_t.slice(_i, (_i + 8)) === "midnight") && !_nvIsLetter(_t.at((_i + 8))))) {
          _out.push({"h": 0, "m": 0, "mer": "am"});
          _i += 8;
          continue;
        }
      }
      _i += 1;
      continue;
    }
    let _j = _i;
    let _numStr = "";
    while (((_j < _n) && (_digits.indexOf(_t.at(_j)) >= 0))) {
      _numStr = (_numStr + _t.at(_j));
      _j += 1;
    }
    let _h = parseInt(_numStr);
    let _m = (-1);
    let _k = _j;
    if (((_k < _n) && (_t.at(_k) === ":"))) {
      let _mm = "";
      let _k2 = (_k + 1);
      while ((((_k2 < _n) && (_digits.indexOf(_t.at(_k2)) >= 0)) && (_mm.length < 2))) {
        _mm = (_mm + _t.at(_k2));
        _k2 += 1;
      }
      if (((_mm.length === 2) && !((_k2 < _n) && (_digits.indexOf(_t.at(_k2)) >= 0)))) {
        _m = parseInt(_mm);
        _k = _k2;
      }
    }
    let _k3 = _k;
    while (((_k3 < _n) && (_t.at(_k3) === " "))) {
      _k3 += 1;
    }
    let _mer = "";
    if ((_t.slice(_k3, (_k3 + 4)) === "a.m.")) {
      _mer = "am";
    } else if ((_t.slice(_k3, (_k3 + 4)) === "p.m.")) {
      _mer = "pm";
    } else if (((_t.slice(_k3, (_k3 + 2)) === "am") && !_nvIsLetter(_t.at((_k3 + 2))))) {
      _mer = "am";
    } else if (((_t.slice(_k3, (_k3 + 2)) === "pm") && !_nvIsLetter(_t.at((_k3 + 2))))) {
      _mer = "pm";
    }
    if (((((((_m >= 0) && (_m <= 59)) || _mer) && (_h >= 0)) && (_h <= 24)) && (_numStr.length <= 2))) {
      _out.push({"h": _h, "m": ((_m >= 0) ? _m : 0), "mer": _mer});
    }
    _i = ((_k > _j) ? _k : _j);
  }
  return _out;
}
function _nvTimesCompatible(a, b) {
  if ((!_jac.builtin.bool(a) || !_jac.builtin.bool(b))) {
    return false;
  }
  if ((a.m !== b.m)) {
    return false;
  }
  let _aSet = _nvHours24(a);
  let _bSet = _nvHours24(b);
  for (const _h of _aSet) {
    if (_bSet.includes(_h)) {
      return true;
    }
  }
  return false;
}
function _nvHours24(tok) {
  let _h = (_jac.poly.and_(tok, () => (tok.h !== undefined)) ? tok.h : 0);
  let _mer = (_jac.poly.and_(tok, () => tok.mer) ? String(tok.mer) : "");
  if ((_mer === "am")) {
    return [(_h % 12)];
  }
  if ((_mer === "pm")) {
    return [((_h % 12) + 12)];
  }
  if ((_h > 12)) {
    return [_h];
  }
  if ((_h === 12)) {
    return [0, 12];
  }
  return [_h, (_h + 12)];
}
function _nvParseClock(s) {
  let _toks = _nvExtractTimeTokens(_nvNorm(s));
  if ((_toks.length > 0)) {
    return _toks[0];
  }
  return null;
}
function _nvIsoPlusDays(iso, n) {
  let _s = String((iso ? iso : ""));
  if ((_s.length < 10)) {
    return "";
  }
  let _dt = Reflect.construct(Date, [(_s.slice(0, 10) + "T00:00:00Z")]);
  let _ms = _dt.getTime();
  if ((_ms !== _ms)) {
    return "";
  }
  _dt.setUTCDate((_dt.getUTCDate() + n));
  return _dt.toISOString().slice(0, 10);
}
function _nvMonthNames() {
  return ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];
}
function _nvDateForms(iso) {
  let _s = String((iso ? iso : "")).slice(0, 10);
  if ((_s.length < 10)) {
    return [];
  }
  let _out = [_s];
  let _short = _nvNorm(shortDateLabel(_s));
  if ((_short && (_short !== _s))) {
    _out.push(_short);
  }
  let _mi = parseInt(_s.slice(5, 7));
  let _d = parseInt(_s.slice(8, 10));
  if (((((_mi === _mi) && (_d === _d)) && (_mi >= 1)) && (_mi <= 12))) {
    let _months = _nvMonthNames();
    _out.push(((_months.at((_mi - 1)) + " ") + String(_d)));
    _out.push(((String(_d) + " ") + _months.at((_mi - 1))));
    _out.push(((String(_d) + " ") + _months.at((_mi - 1)).slice(0, 3)));
  }
  return _out;
}
function _nvCanonDay(dayName) {
  let _toks = _nvExtractDayTokens(_nvNorm(dayName));
  if ((_toks.length > 0)) {
    return _toks[0];
  }
  return "";
}
function _nvHasWord(lowText, word) {
  let _t = String((lowText ? lowText : ""));
  let _w = String((word ? word : ""));
  if (!_w) {
    return false;
  }
  let _from = 0;
  while ((_from < _t.length)) {
    let _i = _t.indexOf(_w, _from);
    if ((_i < 0)) {
      return false;
    }
    let _beforeOk = ((_i === 0) || !_nvIsLetter(_t.at((_i - 1))));
    let _afterOk = !_nvIsLetter(_t.at((_i + _w.length)));
    if ((_beforeOk && _afterOk)) {
      return true;
    }
    _from = (_i + 1);
  }
  return false;
}
function _nvDayCovered(lowNarr, narrDayToks, placed, todayIso) {
  if (!_jac.builtin.bool(placed)) {
    return false;
  }
  let _date = (placed.date ? String(placed.date).slice(0, 10) : "");
  let _wanted = [];
  let _c1 = _nvCanonDay(placed.day_name);
  if (_c1) {
    _wanted.push(_c1);
  }
  if (_date) {
    let _c2 = _nvCanonDay(dayNameFromIso(_date));
    if ((_c2 && !_wanted.includes(_c2))) {
      _wanted.push(_c2);
    }
  }
  for (const _w of _wanted) {
    if (narrDayToks.includes(_w)) {
      return true;
    }
  }
  if (_date) {
    for (const _form of _nvDateForms(_date)) {
      if ((lowNarr.indexOf(_form) >= 0)) {
        return true;
      }
    }
    if (todayIso) {
      if (((_date === String(todayIso)) && (_nvHasWord(lowNarr, "today") || _nvHasWord(lowNarr, "tonight")))) {
        return true;
      }
      if (((_date === _nvIsoPlusDays(String(todayIso), 1)) && _nvHasWord(lowNarr, "tomorrow"))) {
        return true;
      }
    }
  }
  if (((_wanted.length === 0) && !_date)) {
    return true;
  }
  return false;
}
function _nvFactClock24(s) {
  let _tok = _nvParseClock(s);
  if ((_tok === null)) {
    return null;
  }
  let _mer = (_tok.mer ? String(_tok.mer) : "");
  let _h = _tok.h;
  if ((_mer === "am")) {
    _h = (_h % 12);
  } else if ((_mer === "pm")) {
    _h = ((_h % 12) + 12);
  }
  return {"h": _h, "m": _tok.m};
}
function _nvDaypartWords(h24) {
  if ((h24 < 12)) {
    return ["morning"];
  }
  if ((h24 < 17)) {
    return ["afternoon", "midday"];
  }
  return ["evening", "tonight", "night"];
}
function _nvTimeCovered(narrTimeToks, startClock, lowNarr = "", title = "") {
  let _want = _nvParseClock(startClock);
  if ((_want === null)) {
    return true;
  }
  for (const _tok of narrTimeToks) {
    if (_nvTimesCompatible(_tok, _want)) {
      return true;
    }
  }
  if ((narrTimeToks.length > 0)) {
    return false;
  }
  let _fc = _nvFactClock24(startClock);
  if ((_fc === null)) {
    return false;
  }
  let _words = _nvDaypartWords(_fc.h);
  let _t = _nvNorm(title).trim();
  let _all = String((lowNarr ? lowNarr : ""));
  if (!_all) {
    return false;
  }
  let _sep = String.fromCharCode(1);
  let _splitTxt = _all;
  for (const _d of [".", "!", "?", ";", String.fromCharCode(10)]) {
    _splitTxt = _jac.poly.call(_splitTxt.split(_d), "join", _sep);
  }
  for (const _sg of _splitTxt.split(_sep)) {
    if ((!_t || (_sg.indexOf(_t) >= 0))) {
      for (const _w of _words) {
        if (_nvHasWord(_sg, _w)) {
          return true;
        }
      }
    }
  }
  return false;
}
function _nvTitleCovered(lowNarr, title) {
  let _t = _nvNorm(title).trim();
  if (!_t) {
    return true;
  }
  return (lowNarr.indexOf(_t) >= 0);
}
function _nvFactsCorpus(facts) {
  if (!_jac.builtin.bool(facts)) {
    facts = {};
  }
  let _parts = [];
  let _created = (facts.created ? facts.created : []);
  let _moved = (facts.moved ? facts.moved : []);
  let _failed = (facts.failed ? facts.failed : []);
  let _overflow = (facts.overflow ? facts.overflow : []);
  for (const _c of _created) {
    if (!_c) {
      continue;
    }
    if (_c.title) {
      _parts.push(String(_c.title));
    }
    let _p = (_c.placed ? _c.placed : null);
    if ((_p !== null)) {
      if (_p.day_name) {
        _parts.push(String(_p.day_name));
      }
      if (_p.date) {
        for (const _f of _nvDateForms(String(_p.date))) {
          _parts.push(_f);
        }
        let _dn = dayNameFromIso(String(_p.date).slice(0, 10));
        if (_dn) {
          _parts.push(_dn);
        }
      }
      if (_p.start) {
        _parts.push(String(_p.start));
      }
      if (_p.end) {
        _parts.push(String(_p.end));
      }
    }
  }
  for (const _mv of _moved) {
    if (!_mv) {
      continue;
    }
    if (_mv.title) {
      _parts.push(String(_mv.title));
    }
    if (_mv.reason) {
      _parts.push(String(_mv.reason));
    }
    for (const _slotKey of ["before", "after"]) {
      let _slot = _jac.poly.getitem(_mv, _slotKey);
      if (((_slot !== undefined) && (_slot !== null))) {
        if (_slot.day_name) {
          _parts.push(String(_slot.day_name));
        }
        if (_slot.date) {
          for (const _f2 of _nvDateForms(String(_slot.date))) {
            _parts.push(_f2);
          }
          let _dn2 = dayNameFromIso(String(_slot.date).slice(0, 10));
          if (_dn2) {
            _parts.push(_dn2);
          }
        }
        if (_slot.start) {
          _parts.push(String(_slot.start));
        }
        if (_slot.end) {
          _parts.push(String(_slot.end));
        }
      }
    }
  }
  for (const _fl of _failed) {
    if (!_fl) {
      continue;
    }
    if (_fl.title) {
      _parts.push(String(_fl.title));
    }
    if (_fl.reason) {
      _parts.push(String(_fl.reason));
    }
  }
  for (const _ot of _overflow) {
    if (_ot) {
      _parts.push(String(_ot));
    }
  }
  let _opt = (facts.optimize ? facts.optimize : null);
  if ((_opt !== null)) {
    let _ovt = (_opt.overflow_titles ? _opt.overflow_titles : []);
    for (const _ot2 of _ovt) {
      if (_ot2) {
        _parts.push(String(_ot2));
      }
    }
    if (((_opt.placed_n !== undefined) && (_opt.placed_n !== null))) {
      _parts.push(String(_opt.placed_n));
    }
    if (((_opt.total_n !== undefined) && (_opt.total_n !== null))) {
      _parts.push(String(_opt.total_n));
    }
  }
  if (facts.answered) {
    _parts.push(String(facts.answered));
  }
  let _nl = String.fromCharCode(10);
  return _nvNorm(_jac.poly.call(_parts, "join", _nl));
}
function _nvAllFactTitles(facts) {
  if (!_jac.builtin.bool(facts)) {
    facts = {};
  }
  let _out = [];
  for (const _grp of [(facts.created ? facts.created : []), (facts.moved ? facts.moved : []), (facts.failed ? facts.failed : [])]) {
    for (const _it of _grp) {
      if ((_it && _it.title)) {
        let _t = _nvNorm(_it.title).trim();
        if ((_t && !_out.includes(_t))) {
          _out.push(_t);
        }
      }
    }
  }
  for (const _ot of (facts.overflow ? facts.overflow : [])) {
    let _t2 = _nvNorm(_ot).trim();
    if ((_t2 && !_out.includes(_t2))) {
      _out.push(_t2);
    }
  }
  let _opt = (facts.optimize ? facts.optimize : null);
  if ((_opt !== null)) {
    for (const _ot3 of (_opt.overflow_titles ? _opt.overflow_titles : [])) {
      let _t3 = _nvNorm(_ot3).trim();
      if ((_t3 && !_out.includes(_t3))) {
        _out.push(_t3);
      }
    }
  }
  return _out;
}
function _nvTitleMatchesFacts(candidate, factTitles) {
  let _c = _nvNorm(candidate).trim();
  while (((_c.length > 0) && (".,!?;: ".indexOf(_jac.poly.getitem(_c, (_c.length - 1))) >= 0))) {
    _c = _c.slice(0, (_c.length - 1));
  }
  if (!_c) {
    return false;
  }
  for (const _t of factTitles) {
    if ((_c === _t)) {
      return true;
    }
    if (((_c.indexOf(_t) >= 0) || (_t.indexOf(_c) >= 0))) {
      return true;
    }
  }
  return false;
}
function verifyNarration(narration, facts, todayIso = "") {
  if (!_jac.builtin.bool(facts)) {
    facts = {};
  }
  let _todayIso = String((todayIso ? todayIso : ""));
  let _missing = [];
  let _invented = [];
  let _raw = String((((narration !== undefined) && (narration !== null)) ? narration : ""));
  if (!_raw.trim()) {
    return {"ok": false, "missing": ["narration:empty"], "invented": []};
  }
  let _low = _nvNorm(_raw);
  let _created = (facts.created ? facts.created : []);
  let _moved = (facts.moved ? facts.moved : []);
  let _failed = (facts.failed ? facts.failed : []);
  let _overflow = (facts.overflow ? facts.overflow : []);
  let _opt = (facts.optimize ? facts.optimize : null);
  let _narrDays = _nvExtractDayTokens(_low);
  let _narrTimes = _nvExtractTimeTokens(_low);
  for (const _c of _created) {
    if (!_c) {
      continue;
    }
    if (!_nvTitleCovered(_low, _c.title)) {
      _missing.push(("title:" + String(_c.title)));
      continue;
    }
    let _p = (_c.placed ? _c.placed : null);
    if ((_p !== null)) {
      if (!_nvDayCovered(_low, _narrDays, _p, _todayIso)) {
        _missing.push(("day:" + String(_c.title)));
      }
      if (!_nvTimeCovered(_narrTimes, _p.start, _low, _c.title)) {
        _missing.push(("time:" + String(_c.title)));
      }
    }
  }
  for (const _mv of _moved) {
    if (!_mv) {
      continue;
    }
    if (!_nvTitleCovered(_low, _mv.title)) {
      _missing.push(("title:" + String(_mv.title)));
      continue;
    }
    let _aft = (_mv.after ? _mv.after : null);
    if ((_aft !== null)) {
      if (!_nvDayCovered(_low, _narrDays, _aft, _todayIso)) {
        _missing.push(("day:" + String(_mv.title)));
      }
      if (!_nvTimeCovered(_narrTimes, _aft.start, _low, _mv.title)) {
        _missing.push(("time:" + String(_mv.title)));
      }
    }
    let _bef = (_mv.before ? _mv.before : null);
    if (((_bef !== null) && _bef.start)) {
      let _wantBefore = _nvParseClock(_bef.start);
      if ((_wantBefore !== null)) {
        let _triggers = ["was ", "from ", "previously at ", "previously ", "before:"];
        let _foundWasClock = false;
        let _allWasClocksOk = true;
        let _ti = 0;
        while ((_ti < _triggers.length)) {
          let _trig = _triggers.at(_ti);
          let _from2 = 0;
          while ((_from2 < _low.length)) {
            let _pos = _low.indexOf(_trig, _from2);
            if ((_pos < 0)) {
              _from2 = _low.length;
              continue;
            }
            let _window = _low.slice((_pos + _trig.length), ((_pos + _trig.length) + 20));
            let _wToks = _nvExtractTimeTokens(_window);
            if ((_wToks.length > 0)) {
              _foundWasClock = true;
              let _compat = false;
              for (const _wt of _wToks) {
                if (_nvTimesCompatible(_wt, _wantBefore)) {
                  _compat = true;
                }
              }
              if (!_compat) {
                _allWasClocksOk = false;
              }
            }
            _from2 = (_pos + 1);
          }
          _ti += 1;
        }
        if ((_foundWasClock && !_allWasClocksOk)) {
          _invented.push(("before_time:" + String(_mv.title)));
        }
      }
    }
  }
  for (const _fl of _failed) {
    if ((_fl && !_nvTitleCovered(_low, _fl.title))) {
      _missing.push(("title:" + String(_fl.title)));
    }
  }
  for (const _ot of _overflow) {
    if ((_ot && !_nvTitleCovered(_low, _ot))) {
      _missing.push(("overflow:" + String(_ot)));
    }
  }
  if ((_opt !== null)) {
    for (const _ot2 of (_opt.overflow_titles ? _opt.overflow_titles : [])) {
      if ((_ot2 && !_nvTitleCovered(_low, _ot2))) {
        _missing.push(("overflow:" + String(_ot2)));
      }
    }
    let _alreadySet = (_opt.already_set === true);
    let _pn = (((_opt.placed_n !== undefined) && (_opt.placed_n !== null)) ? _opt.placed_n : 0);
    if (((_pn > 0) && !_alreadySet)) {
      let _pnStr = String(_pn);
      if ((_low.indexOf(_pnStr) < 0)) {
        _missing.push(("optimize_count:" + _pnStr));
      }
    }
    if (_alreadySet) {
      for (const _noOpVb of ["placed", "have been placed", "has been placed", "added to", "scheduled for you", "moved to", "penciled in", "pencilled in"]) {
        if ((_low.indexOf(_noOpVb) >= 0)) {
          _invented.push(("no_op_claim:" + _noOpVb));
        }
      }
    }
  }
  let _corpus = _nvFactsCorpus(facts);
  let _allowedDays = _nvExtractDayTokens(_corpus);
  let _allowedTimes = _nvExtractTimeTokens(_corpus);
  for (const _d of _narrDays) {
    if (!_allowedDays.includes(_d)) {
      _invented.push(("day:" + _d));
    }
  }
  let _factDates = [];
  for (const _grpC of _created) {
    if (((_grpC && _grpC.placed) && _grpC.placed.date)) {
      _factDates.push(String(_grpC.placed.date).slice(0, 10));
    }
  }
  for (const _grpM of _moved) {
    if (((_grpM && _grpM.after) && _grpM.after.date)) {
      _factDates.push(String(_grpM.after.date).slice(0, 10));
    }
    if (((_grpM && _grpM.before) && _grpM.before.date)) {
      _factDates.push(String(_grpM.before.date).slice(0, 10));
    }
  }
  for (const _rel of ["today", "tonight", "tomorrow"]) {
    if ((_nvHasWord(_low, _rel) && !_nvHasWord(_corpus, _rel))) {
      let _aliasIso = ((_rel === "tomorrow") ? _nvIsoPlusDays(_todayIso, 1) : _todayIso);
      if (!(_aliasIso && _factDates.includes(_aliasIso))) {
        _invented.push(("relative:" + _rel));
      }
    }
  }
  for (const _tk of _narrTimes) {
    let _okT = false;
    for (const _atk of _allowedTimes) {
      if (_nvTimesCompatible(_tk, _atk)) {
        _okT = true;
      }
    }
    if (!_okT) {
      _invented.push((((("time:" + String(_tk.h)) + ":") + ((_tk.m < 10) ? ("0" + String(_tk.m)) : String(_tk.m))) + (_tk.mer ? _tk.mer : "")));
    }
  }
  let _factTitles = _nvAllFactTitles(facts);
  let _q = String.fromCharCode(34);
  let _qParts = _low.split(_q);
  let _qi = 1;
  while ((_qi < _qParts.length)) {
    if (((_jac.int.mod(_qi, 2) === 1) && (_qi < (_qParts.length - (((_qParts.length % 2) === 1) ? 0 : 1))))) {
      let _span = _qParts.at(_qi).trim();
      if (((_span.length >= 2) && (_span.length <= 80))) {
        if ((!_nvTitleMatchesFacts(_span, _factTitles) && (_corpus.indexOf(_span) < 0))) {
          _invented.push(("claim:" + _span));
        }
      }
    }
    _qi += 1;
  }
  let _createdFamilyOk = ((_created.length > 0) || (_failed.length > 0));
  let _movedFamilyOk = (_moved.length > 0);
  for (const _vb of ["added", "created", "scheduled", "booked"]) {
    if (((_nvHasWord(_low, _vb) && !_createdFamilyOk) && !_nvHasWord(_corpus, _vb))) {
      _invented.push(("verb:" + _vb));
    }
  }
  for (const _vb4 of ["penciled", "pencilled"]) {
    if ((((_nvHasWord(_low, _vb4) && !_createdFamilyOk) && !_movedFamilyOk) && !_nvHasWord(_corpus, _vb4))) {
      _invented.push(("verb:" + _vb4));
    }
  }
  for (const _vb2 of ["moved", "rescheduled", "shifted", "swapped"]) {
    if (((_nvHasWord(_low, _vb2) && !_movedFamilyOk) && !_nvHasWord(_corpus, _vb2))) {
      _invented.push(("verb:" + _vb2));
    }
  }
  for (const _vb3 of ["deleted", "removed", "cancelled", "canceled", "cleared"]) {
    if ((_nvHasWord(_low, _vb3) && !_nvHasWord(_corpus, _vb3))) {
      _invented.push(("verb:" + _vb3));
    }
  }
  let _nl = String.fromCharCode(10);
  for (const _ln of _low.split(_nl)) {
    let _s = _ln.trim();
    for (const _op of ["added task:", "added event:", "added fixed event:", "penciled in:", "pencilled in:", "didn't fit:", "moved:", "rescheduled:"]) {
      if ((_s.indexOf(_op) === 0)) {
        let _rest = _s.slice(_op.length).trim();
        for (const _stop of [String.fromCharCode(8212), " - ", "(", ".", ","]) {
          let _si = _rest.indexOf(_stop);
          if ((_si >= 0)) {
            _rest = _rest.slice(0, _si);
          }
        }
        _rest = _rest.trim();
        if ((_rest && !_nvTitleMatchesFacts(_rest, _factTitles))) {
          _invented.push(("claim:" + _rest));
        }
      }
    }
  }
  return {"ok": ((_missing.length === 0) && (_invented.length === 0)), "missing": _missing, "invented": _invented};
}
export {_nvAllFactTitles, _nvCanonDay, _nvDateForms, _nvDayCovered, _nvDaypartWords, _nvExtractDayTokens, _nvExtractTimeTokens, _nvFactClock24, _nvFactsCorpus, _nvHasWord, _nvHours24, _nvIsLetter, _nvIsoPlusDays, _nvMonthNames, _nvNorm, _nvParseClock, _nvTimeCovered, _nvTimesCompatible, _nvTitleCovered, _nvTitleMatchesFacts, verifyNarration};
//# sourceMappingURL=narration_verifier.js.map
